import { AdHocEventFormState, AdhocCalendarEvent } from 'src/calendar/types';
import { FullBoatDetail } from 'src/types/boat/BoatDetail';
import differenceInDays from 'date-fns/differenceInDays';
import { newTZDate } from '../helpers';
import {
  UNAVAILABLE_EDITOR_END_DATE as END_DATE,
  UNAVAILABLE_EDITOR_LISTING_IDS as LISTING_IDS,
  UNAVAILABLE_EDITOR_REPEAT_END_DATE as REPEAT_END_DATE,
  UNAVAILABLE_EDITOR_REPEAT_FREQUENCY as REPEAT_FREQUENCY,
  UNAVAILABLE_EDITOR_SELECT_ALL_LISTINGS as SELECT_ALL_LISTINGS,
  UNAVAILABLE_EDITOR_START_DATE as START_DATE,
  UNAVAILABLE_EDITOR_START_TIME as START_TIME,
  UNAVAILABLE_EDITOR_END_TIME as END_TIME,
  UNAVAILABLE_EDITOR_ALL_DAY as ALL_DAY,
} from '../constants';

/**
 * Creates initial form values for an ad-hoc calendar event editor
 */
export const adHocEventInitialValues = (
  adHocEvent: AdhocCalendarEvent | null,
  boats: FullBoatDetail[],
  today: string,
  selectedDate: string,
  filteredListings: string[],
): AdHocEventFormState => {
  const useSelectedDate = (
    selectedDate && differenceInDays(newTZDate(selectedDate), newTZDate(today)) >= 0
  );
  const date = useSelectedDate ? selectedDate : today;

  const initialListingIds = () => {
    if (filteredListings.length > 0) {
      // If any filters are set, find the primary id of the matching hashed id
      const result: number[] = [];
      filteredListings.forEach(listing => {
        const match = boats.find(boat => boat.id === listing);
        if (match?.boat_id) {
          result.push(match.boat_id);
        }
      });
      return result;
    }
    if (boats.length === 1) {
      return boats.map(b => b.boat_id);
    }
    return adHocEvent?.[LISTING_IDS] ?? boats.map(b => b.boat_id);
  };

  return {
    id: adHocEvent?.id,
    [START_DATE]: adHocEvent?.[START_DATE] ?? date,
    [END_DATE]: adHocEvent?.[END_DATE] ?? date,
    frequency: adHocEvent?.calendar_events && adHocEvent?.calendar_events?.length > 1
      ? adHocEvent?.[REPEAT_FREQUENCY]
      : undefined,
    repetitions_end_date: adHocEvent?.[REPEAT_END_DATE] ?? date,
    repeat_count: adHocEvent?.calendar_events?.length ?? 1,
    [SELECT_ALL_LISTINGS]: adHocEvent?.[LISTING_IDS]?.length === boats.length,
    [LISTING_IDS]: initialListingIds(),
    [START_TIME]: adHocEvent?.event_start_time ?? '',
    [END_TIME]: adHocEvent?.event_end_time ?? '',
    [ALL_DAY]: adHocEvent?.[ALL_DAY] as boolean ?? true,
  };
};

/**
 * Prepares form data for API submission
 */
export const prepareAdHocEventDataForAPI = (values: AdHocEventFormState): AdhocCalendarEvent => ({
  id: values.id,
  all_day: values.all_day,
  type: 'unavailable',
  event_date: values.event_date,
  event_end_date: values.event_end_date,
  frequency: (values.frequency && values.repeat_count > 1)
    ? values.frequency : null,
  repetitions_end_date: (values.frequency && values.repeat_count > 1)
    ? values.repetitions_end_date : null,
  boat_ids: values.boat_ids,
  event_start_time: !values.all_day && values.event_start_time
    ? values.event_start_time
    : undefined,
  event_end_time: !values.all_day && values.event_end_time ? values.event_end_time : undefined,
});

/**
 * Validates the ad-hoc event form
 */
export const validateAdHocEvent = (values: AdHocEventFormState): Record<string, any> => {
  const errors: Record<string, any> = {};
  const boatIds = values.boat_ids.length;

  if (!boatIds) {
    errors.formError = 'Select a listing to mark as unavailable.';
    if (!values.all_day && (!values.event_start_time || !values.event_end_time)) {
      errors.formError = 'Please select the time and listing to proceed.';
    }
  } else if (!values.all_day) {
    if (!values.event_start_time) {
      errors.event_start_time = 'Select time';
      errors.formError = 'Please select the time you want to block.';
    } else if (!values.event_end_time) {
      errors.event_end_time = 'Select time';
      errors.formError = 'Please select the time you want to block.';
    }
    if (
      values.event_start_time
      && values.event_end_time
      && values.event_date === values.event_end_date
      && values.event_start_time >= values.event_end_time) {
      errors.formError = 'End time should be after start time';
    }
  }

  return errors;
};
